import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { articles as initialArticles } from '../data/articles';
import { useMetaTags } from '../hooks/useMetaTags';
import './BlogPostPage.css';

function sanitizeArticleHtml(html) {
  if (!html) return '';
  return html
    .replace(/href="https:\/\/(www\.)?ilovethisgame\.ca\/products\/playermaker-2-0"/g, 'href="/#product-showcase"')
    .replace(/href="https:\/\/(www\.)?ilovethisgame\.ca\/pages\/how-it-works"/g, 'href="/#how-it-works"')
    .replace(/href="https:\/\/(www\.)?ilovethisgame\.ca\/pages\/for-teams"/g, 'href="/#features"')
    .replace(/href="https:\/\/(www\.)?ilovethisgame\.ca\/blogs\/news\/[^"]*"/g, 'href="/blog"')
    .replace(/href="https:\/\/(www\.)?ilovethisgame\.ca\/?(?=[ "'])/g, 'href="/"');
}

function SocialShareBar({ title }) {
  const [copied, setCopied] = React.useState(false);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : 'https://ilovethisgame.ca/blog';
  const shareTitle = title || 'Check out this article on ILTG';

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `Read "${shareTitle}" on ILTG:`,
          url: shareUrl,
        });
      } catch (err) {
        console.log('User cancelled share', err);
      }
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleInstagramShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
      setCopied('instagram');
      setTimeout(() => setCopied(false), 3000);
    }
    window.open('https://www.instagram.com/', '_blank');
  };

  return (
    <div className="social-share-bar">
      <span className="share-label">SHARE ARTICLE:</span>
      
      {/* Native System Share Trigger (Square box with arrow pointing up) */}
      <button onClick={handleShare} className="share-native-btn" title="Share via native OS menu">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
          <polyline points="16 6 12 2 8 6"/>
          <line x1="12" y1="2" x2="12" y2="15"/>
        </svg>
        <span>{copied === true ? 'Link Copied! ✓' : 'Share Article'}</span>
      </button>

      {/* Desktop Direct Buttons */}
      <div className="share-buttons">
        <a 
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="share-btn share-x" 
          title="Share on X (Twitter)"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          <span>X</span>
        </a>
        <a 
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="share-btn share-facebook" 
          title="Share on Facebook"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          <span>Facebook</span>
        </a>
        <a 
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="share-btn share-linkedin" 
          title="Share on LinkedIn"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          <span>LinkedIn</span>
        </a>
        <button 
          onClick={handleInstagramShare} 
          className="share-btn share-instagram" 
          title="Copy Link for Instagram Story/Bio"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
          <span>Instagram</span>
        </button>
      </div>
      {copied === 'instagram' && (
        <span className="share-toast">Link copied! Opening Instagram...</span>
      )}
    </div>
  );
}

export default function BlogPostPage({ articleId, articles = initialArticles, onNavigate }) {
  const navigate = useNavigate();
  const article = articles.find(a => a.id === articleId) || articles[0];

  const articleSchema = React.useMemo(() => {
    if (!article) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.excerpt,
      image: article.heroImage ? `https://ilovethisgame.ca${article.heroImage}` : 'https://ilovethisgame.ca/playermaker_box_transparent.webp',
      author: {
        '@type': 'Person',
        name: (!article.author || article.author.includes('AI')) ? 'Harry Johnston' : article.author,
        jobTitle: (!article.authorRole || article.authorRole.includes('Tactical')) ? 'Senior Sports Writer' : article.authorRole
      },
      publisher: {
        '@type': 'Organization',
        name: 'iLOVEthisgame.ca',
        logo: 'https://ilovethisgame.ca/logo.webp'
      },
      datePublished: article.date,
      mainEntityOfPage: `https://ilovethisgame.ca/blog/${article.id}`
    };
  }, [article]);

  useMetaTags({
    title: `${article.title} | Playermaker Canada`,
    description: article.excerpt,
    image: article.heroImage,
    url: `/blog/${article.id}`,
    type: 'article',
    articleSchema
  });

  React.useEffect(() => {
    const images = document.querySelectorAll('.post-body-text img');
    images.forEach(img => {
      img.onerror = () => {
        img.src = '/blog-images/nano_banana_generated-aed095a8adef21cfe104.png';
        img.onerror = null;
      };
    });
  }, [articleId, article]);

  const relatedArticles = articles
    .filter(a => a.id !== article.id)
    .slice(0, 3);

  return (
    <div className="blog-post-wrapper">
      <div className="container">

        {/* Back navigation */}
        <div className="post-back-bar">
          <Link to="/blog" className="btn btn-outline post-back-btn">
            &larr; Back to All Articles
          </Link>
        </div>

        {/* Article Header */}
        <article className="post-main-content">
          <header className="post-header text-center">
            <span className="badge-volt">{article.category}</span>
            <h1 className="post-title">{article.title}</h1>
            
            <div className="post-byline">
              <div className="author-info" style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
                <img 
                  src={article.authorAvatar || "/harry_johnston.webp"} 
                  alt={article.author || "Harry Johnston"} 
                  style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--accent-volt)', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))' }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className="author-name">{(!article.author || article.author.includes('AI')) ? "Harry Johnston" : article.author}</span>
                  <span className="author-role">{(!article.authorRole || article.authorRole.includes('Tactical')) ? "Senior Sports Writer" : article.authorRole}</span>
                </div>
              </div>
              <div className="post-meta-details" style={{ marginTop: '0.4rem' }}>
                <span>{article.date}</span>
                <span>•</span>
                <span>{article.readTime}</span>
              </div>
            </div>

            <SocialShareBar title={article.title} />
          </header>

          {/* Hero Banner Image */}
          {article.heroImage && (
            <div className="post-hero-image-wrap">
              <img src={article.heroImage} alt={article.heroImageAlt || `Playermaker Canada, ${article.title}`} className="post-hero-image" fetchPriority="high" />
            </div>
          )}

          {/* Article Excerpt Callout */}
          <div className="post-excerpt-callout card-tech">
            <p>{article.excerpt}</p>
          </div>

          {/* Article Body Content */}
          {/<[a-z][\s\S]*>/i.test(article.content) ? (
            <div className="post-body-text" dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(article.content) }} />
          ) : (
            <div className="post-body-text">
            {article.content.split('\n\n').map((paragraph, idx) => {
              if (paragraph.startsWith('### ')) {
                return <h3 key={idx} className="post-heading-3">{paragraph.replace('### ', '')}</h3>;
              } else if (paragraph.startsWith('1. ') || paragraph.startsWith('- ')) {
                const items = paragraph.split('\n');
                return (
                  <ul key={idx} className="post-list">
                    {items.map((item, i) => (
                      <li key={i}>{item.replace(/^[0-9]+\.\s*|-s*/, '')}</li>
                    ))}
                  </ul>
                );
              } else {
                return <p key={idx}>{paragraph}</p>;
              }
            })}
          </div>
          )}

          {/* Share & Support Footer */}
          <div className="post-share-footer card-tech">
            <div>
              <h4>SHARE THIS ARTICLE</h4>
              <p>Pass insights to your teammates, coaches, or parents.</p>
              <div style={{ marginTop: '1.2rem' }}>
                <SocialShareBar title={article.title} />
              </div>
            </div>
            <div className="post-share-actions">
              <a href="#product-showcase" onClick={(e) => { e.preventDefault(); if (onNavigate) onNavigate('/#product-showcase'); }} className="btn btn-volt">
                Shop Playermaker 2.0 &rarr;
              </a>
            </div>
          </div>
        </article>

        {/* Related Articles Section */}
        <section className="related-articles-section">
          <h3 className="related-title">MORE FROM ILTG INSIGHTS</h3>
          <div className="blog-grid">
            {relatedArticles.map(rel => {
              const handleCardClick = (e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
                if (onNavigate) {
                  onNavigate(`/blog/${rel.id}`);
                } else {
                  navigate(`/blog/${rel.id}`);
                }
              };
              return (
                <article key={rel.id} className="card-tech blog-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
                  {(rel.heroImage || rel.image) && (
                    <div className="blog-card-image-wrap">
                      <img 
                        src={rel.heroImage || rel.image} 
                        alt={rel.heroImageAlt || `Playermaker Canada, ${rel.title}`} 
                        className="blog-card-image"
                        loading="lazy" 
                      />
                    </div>
                  )}
                  <div className="blog-card-content">
                    <div className="blog-meta">
                      <span className="blog-category">{rel.category}</span>
                      <span className="blog-read-time">{rel.readTime}</span>
                    </div>
                    <h4 className="blog-card-title">
                      <a href={`/blog/${rel.id}`} onClick={handleCardClick}>
                        {rel.title}
                      </a>
                    </h4>
                    <p className="blog-excerpt">{rel.excerpt}</p>
                    <div className="blog-footer">
                      <span className="blog-date">{rel.date}</span>
                      <span className="blog-read-link">Read Article &rarr;</span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

      </div>
    </div>
  );
}
